import { audioStore } from "~/components/audio/audioStore";
import { AnalyticsEvents, trackEvent } from "~/connectors/analytics.connector";
import { SITE_CONFIG } from "~/lib/constants";
import { audioPlayer } from "~/services/audioPlayer.service";
import type { Song } from "~/types";

class CastService {
  initialized = false;
  private sdkLoaded = false;
  private remotePlayer: cast.framework.RemotePlayer | null = null;
  private controller: cast.framework.RemotePlayerController | null = null;
  private sessionStateHandler: ((e: { sessionState: string }) => void) | null =
    null;
  private remotePlayerHandlers = new Map<
    string,
    (
      e: cast.framework.RemotePlayerChangedEvent<cast.framework.RemotePlayer>,
    ) => void
  >();

  /**
   * Lazily load the Cast SDK. Safe to call multiple times — injects the script
   * at most once and registers the global callback before injecting.
   * Called the first time playSong() is invoked.
   */
  loadSdk() {
    if (typeof window === "undefined") return;

    // If the SDK is already present (e.g. browser cache), init immediately.
    if (window.cast && window.cast.framework) {
      try {
        this.init();
      } catch (e) {
        /* ignore */
      }
      return;
    }

    if (this.sdkLoaded) return;
    this.sdkLoaded = true;

    // Idempotent: skip if another module already injected the script.
    if (document.getElementById("google-cast-sdk")) return;

    // Register the global callback before injecting the script so the SDK
    // can call it synchronously when it finishes loading.
    window.__onGCastApiAvailable = (isAvailable: boolean) => {
      if (!isAvailable) return;
      try {
        if (
          typeof cast !== "undefined" &&
          cast.framework &&
          typeof chrome !== "undefined" &&
          chrome.cast
        ) {
          cast.framework.CastContext.getInstance().setOptions({
            receiverApplicationId:
              chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
            autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
          });
        }
      } catch (e) {
        // ignore
      }
      try {
        this.init();
      } catch (e) {
        /* ignore */
      }
    };

    const script = document.createElement("script");
    script.id = "google-cast-sdk";
    script.src =
      "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
    script.async = true;
    document.body.appendChild(script);
  }

  init() {
    if (this.initialized) return;
    if (typeof window === "undefined") return;
    if (!cast || !cast.framework) return;

    this.initialized = true;
    this.remotePlayer = new cast.framework.RemotePlayer();
    this.controller = new cast.framework.RemotePlayerController(
      this.remotePlayer,
    );

    console.info("[Cast] remotePlayer/controller created");

    // session state handler
    this.sessionStateHandler = (e: { sessionState: string }) => {
      console.debug("[Cast] sessionStateHandler invoked", e);
      // guard runtime availability
      if (typeof cast === "undefined" || !cast.framework) return;
      const state = e.sessionState;
      const ctx = cast.framework.CastContext.getInstance();
      const session = ctx.getCurrentSession();
      console.info("[Cast] session state change", {
        state,
        device: session?.getCastDevice?.()?.friendlyName,
      });
      if (state === cast.framework.SessionState.SESSION_STARTED) {
        audioPlayer.pause();
        audioStore
          .getState()
          .setIsCasting(true, session?.getCastDevice()?.friendlyName ?? null);
        console.info(
          "[Cast] SESSION_STARTED — paused local audio and set isCasting",
        );
        try {
          trackEvent(AnalyticsEvents.SONG_PLAYER_OPEN, {
            device: session?.getCastDevice?.()?.friendlyName ?? null,
            title: audioStore.getState().currentSong?.title ?? null,
            songId: audioStore.getState().currentSong?.id ?? null,
          });
        } catch (err) {
          /* ignore analytics errors */
        }
        const s = audioStore.getState();
        if (s.currentSong) {
          this.loadMedia(
            s.currentSong,
            s.currentTime || 0,
            s.isPlaying || false,
          );
        }
      } else if (state === cast.framework.SessionState.SESSION_RESUMED) {
        audioPlayer.pause();
        audioStore
          .getState()
          .setIsCasting(true, session?.getCastDevice()?.friendlyName ?? null);
        console.info(
          "[Cast] SESSION_RESUMED — paused local audio and refreshed isCasting",
        );
        try {
          trackEvent(AnalyticsEvents.SONG_PLAYER_PLAY, {
            device: session?.getCastDevice?.()?.friendlyName ?? null,
            title: audioStore.getState().currentSong?.title ?? null,
            songId: audioStore.getState().currentSong?.id ?? null,
          });
        } catch (err) {
          /* ignore analytics errors */
        }
      } else if (state === cast.framework.SessionState.SESSION_ENDED) {
        console.info(
          "[Cast] SESSION_ENDED — clearing isCasting; remote saved state:",
          this.remotePlayer?.savedPlayerState,
        );
        audioStore.getState().setIsCasting(false, null);
        // resume local audio at saved position
        const saved = this.remotePlayer?.savedPlayerState;
        const resumeTime =
          saved?.currentTime ?? audioStore.getState().currentTime;
        const shouldPlay = saved ? !saved.isPaused : false;
        const { currentSong } = audioStore.getState();
        if (currentSong) {
          const onDuration = (d: unknown) => {
            audioPlayer.off("duration", onDuration);
            audioPlayer.seek(resumeTime || 0);
            if (shouldPlay) audioPlayer.play();
          };
          audioPlayer.on("duration", onDuration);
          audioPlayer.setSource(currentSong.audioSrc);
          try {
            trackEvent(AnalyticsEvents.SONG_PLAYER_CLOSE, {
              device: session?.getCastDevice?.()?.friendlyName ?? null,
              title: currentSong.title,
              songId: currentSong.id,
              resumeTime,
            });
          } catch (err) {
            /* ignore analytics errors */
          }
        }
      }
    };

    cast.framework.CastContext.getInstance().addEventListener(
      cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
      this.sessionStateHandler,
    );

    // remote player handlers to sync state
    const handlers: Partial<
      Record<
        keyof typeof cast.framework.RemotePlayerEventType,
        (e: cast.framework.RemotePlayerChangedEvent<any>) => void
      >
    > = {
      IS_PAUSED_CHANGED: (e) => {
        console.debug("[Cast] event IS_PAUSED_CHANGED", {
          e,
          player: this.remotePlayer,
        });
        if (!audioStore.getState().isCasting) return;
        audioStore.setState({
          isPlaying: !(this.remotePlayer?.isPaused ?? true),
        });
        try {
          const isPaused = !!this.remotePlayer?.isPaused;
          trackEvent(
            isPaused
              ? AnalyticsEvents.SONG_PLAYER_PAUSE
              : AnalyticsEvents.SONG_PLAYER_PLAY,
            {
              device:
                cast.framework.CastContext.getInstance()
                  .getCurrentSession?.()
                  ?.getCastDevice?.()?.friendlyName ?? null,
              currentTime: this.remotePlayer?.currentTime ?? 0,
            },
          );
        } catch (err) {
          /* ignore analytics errors */
        }
      },
      CURRENT_TIME_CHANGED: (e) => {
        console.debug("[Cast] event CURRENT_TIME_CHANGED", {
          e,
          currentTime: this.remotePlayer?.currentTime,
        });
        if (!audioStore.getState().isCasting) return;
        audioStore.setState({
          currentTime: this.remotePlayer?.currentTime ?? 0,
        });
      },
      DURATION_CHANGED: (e) => {
        console.debug("[Cast] event DURATION_CHANGED", {
          e,
          duration: this.remotePlayer?.duration,
        });
        if (!audioStore.getState().isCasting) return;
        audioStore.setState({ duration: this.remotePlayer?.duration ?? 0 });
      },
      PLAYER_STATE_CHANGED: (e) => {
        console.debug("[Cast] event PLAYER_STATE_CHANGED", {
          e,
          playerState: this.remotePlayer?.playerState,
        });
        if (!audioStore.getState().isCasting) return;
        if (typeof cast === "undefined" || !cast.framework) return;
        const state = this.remotePlayer?.playerState ?? null;
        const ctx = cast.framework.CastContext.getInstance();
        const idleReason =
          ctx.getCurrentSession()?.getMediaSession()?.idleReason ?? null;
        console.info("[Cast] PLAYER_STATE_CHANGED details", {
          state,
          idleReason,
        });
        if (state === chrome.cast.media.PlayerState.IDLE) {
          if (idleReason === "FINISHED") {
            try {
              trackEvent(AnalyticsEvents.SONG_PLAYER_NEXT);
            } catch (err) {}
            audioStore.getState().next();
          } else if (idleReason === "ERROR") {
            try {
              trackEvent(AnalyticsEvents.SONG_PLAYER_NEXT);
            } catch (err) {}
            audioStore.getState().next();
          }
        }
      },
      IS_CONNECTED_CHANGED: (e) => {
        console.debug("[Cast] event IS_CONNECTED_CHANGED", {
          e,
          isConnected: this.remotePlayer?.isConnected,
        });
        if (this.remotePlayer && !this.remotePlayer.isConnected) {
          console.info("[Cast] Remote player disconnected");
          try {
            trackEvent(AnalyticsEvents.SONG_PLAYER_CLOSE, {
              device:
                cast.framework.CastContext.getInstance()
                  .getCurrentSession?.()
                  ?.getCastDevice?.()?.friendlyName ?? null,
            });
          } catch (err) {}
        }
      },
    };

    for (const [k, fn] of Object.entries(handlers)) {
      this.remotePlayerHandlers.set(k, fn);
      try {
        const ev =
          cast.framework.RemotePlayerEventType[
            k as keyof typeof cast.framework.RemotePlayerEventType
          ];
        this.controller?.addEventListener(ev, fn);
      } catch (err) {
        console.warn(`[Cast] failed registering ${k} handler`, {
          event: k,
          err,
        });
      }
    }
    console.debug(
      `[Cast] Initialized. Registered handlers: ${Array.from(this.remotePlayerHandlers.keys()).join(", ")}`,
    );
  }

  async loadMedia(song: Song, startTime = 0, autoplay = true) {
    if (typeof window === "undefined") return;
    if (typeof cast === "undefined" || !cast.framework) {
      console.warn("[Cast] SDK not available");
      return;
    }
    const session =
      cast.framework.CastContext.getInstance().getCurrentSession();
    if (!session) {
      console.warn("[Cast] loadMedia called with no active session");
      return;
    }

    try {
      if (typeof chrome === "undefined" || !chrome.cast) {
        console.warn("[Cast] chrome.cast not available");
        return;
      }
      // Resolve the audio URL to an absolute URL for the cast device
      const resolvedUrl = ((): string => {
        try {
          if (song.castAudioUrl) return song.castAudioUrl;
          if (!song.audioSrc) return "";
          if (/^https?:\/\//i.test(song.audioSrc)) return song.audioSrc;
          return (
            window.location.origin.replace(/\/$/, "") +
            (song.audioSrc.startsWith("/")
              ? song.audioSrc
              : "/" + song.audioSrc)
          );
        } catch (e) {
          return song.audioSrc;
        }
      })();
      const resolvedArtworkUrl = ((): string | null => {
        try {
          if (song.castArtworkUrl) return song.castArtworkUrl;
          if (!song.artworkSrc) return null;
          if (/^https?:\/\//i.test(song.artworkSrc)) return song.artworkSrc;
          return (
            window.location.origin.replace(/\/$/, "") +
            (song.artworkSrc.startsWith("/")
              ? song.artworkSrc
              : "/" + song.artworkSrc)
          );
        } catch (e) {
          return song.artworkSrc ?? null;
        }
      })();
      console.info("[Cast] media content URL", {
        audioSrc: song.audioSrc,
        resolvedAudio: resolvedUrl,
        artworkSrc: song.artworkSrc,
        resolvedArtwork: resolvedArtworkUrl,
      });
      const mediaInfo = new chrome.cast.media.MediaInfo(
        resolvedUrl,
        "audio/mpeg",
      );
      const metadata = new chrome.cast.media.MusicTrackMediaMetadata();
      metadata.title = song.title;
      metadata.artist = SITE_CONFIG.name;
      if (resolvedArtworkUrl)
        metadata.images = [new chrome.cast.Image(resolvedArtworkUrl)];
      mediaInfo.metadata = metadata;

      const request = new chrome.cast.media.LoadRequest(mediaInfo);
      request.currentTime = startTime;
      request.autoplay = autoplay;

      console.info(
        `[Cast] Loading media: ${song.title} at ${startTime}s, autoplay=${autoplay}`,
      );
      console.debug("[Cast] request object", { request });
      const res = await session.loadMedia(request);
      try {
        const ctx = cast.framework.CastContext.getInstance();
        const mediaSession = ctx.getCurrentSession?.()?.getMediaSession?.();
        console.info("[Cast] loadMedia success", { res, mediaSession });
        console.info("[Cast] session details", {
          sessionId: ctx.getCurrentSession?.()?.getSessionId?.(),
          device: ctx.getCurrentSession?.()?.getCastDevice?.(),
        });
        if (!mediaSession) {
          console.warn(
            "[Cast] mediaSession is null after load — device may have rejected the URL or media is not playable",
          );
        }
        setTimeout(
          () =>
            console.debug("[Cast] remotePlayer snapshot (+1s)", {
              isConnected: this.remotePlayer?.isConnected,
              isPaused: this.remotePlayer?.isPaused,
              currentTime: this.remotePlayer?.currentTime,
              duration: this.remotePlayer?.duration,
              playerState: this.remotePlayer?.playerState,
            }),
          1000,
        );
        setTimeout(
          () =>
            console.debug("[Cast] remotePlayer snapshot (+3s)", {
              isConnected: this.remotePlayer?.isConnected,
              isPaused: this.remotePlayer?.isPaused,
              currentTime: this.remotePlayer?.currentTime,
              duration: this.remotePlayer?.duration,
              playerState: this.remotePlayer?.playerState,
            }),
          3000,
        );
      } catch (ex) {
        console.info("[Cast] loadMedia success (mediaSession unavailable)", {
          res,
        });
      }
    } catch (err) {
      console.error("[Cast] loadMedia error", err);
      audioStore.getState().next();
    }
  }

  getController(): { player: any; controller: any } | null {
    if (!this.remotePlayer || !this.controller) return null;
    return { player: this.remotePlayer, controller: this.controller };
  }

  dispose() {
    if (this.sessionStateHandler) {
      try {
        if (typeof cast !== "undefined" && cast.framework) {
          cast.framework.CastContext.getInstance().removeEventListener(
            cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
            this.sessionStateHandler,
          );
        }
      } catch (e) {
        /* ignore */
      }
      this.sessionStateHandler = null;
    }
    for (const [k, fn] of this.remotePlayerHandlers.entries()) {
      try {
        if (typeof cast !== "undefined" && cast.framework) {
          this.controller?.removeEventListener(
            cast.framework.RemotePlayerEventType[
              k as keyof typeof cast.framework.RemotePlayerEventType
            ],
            fn,
          );
        }
      } catch (e) {
        /* ignore */
      }
    }
    this.remotePlayerHandlers.clear();
    this.remotePlayer = null;
    this.controller = null;
    this.initialized = false;
    console.info("[Cast] Disposed");
  }
}

export const castService = new CastService();
